<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" id="addDeductionModal" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Deduction</h3>
        </template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="dataLoaded">
                <div class="mb-2" v-if="editingDeductionType">
                    <div class="p-1 unpad-x bg-150 rounded">
                        <div><span class="text-muted">Deduction: </span>{{ displayName(editingDeductionType) }}</div>
                    </div>
                </div>

                <div v-if="step == 'companies'">
                    <p class="mb-0">Select the company or companies these deductions will be available for.</p>
                    <company-selector :client="client" :companies="client.companies" v-model="formData.companies"/>
                </div>

                <div v-if="step == 'deduction-types'">
                    <p>What deduction(s) will be available?</p>
                    <deduction-selector v-model="formData.deduction_types" :selectableDeductions="selectableDeductionTypes" @update:model-value="onUpdateSelectedDeductionTypes($event)" :hideTabs="true"/>
                </div>

                <div v-if="step == 'customize'">
                    <p>Customize these deductions for the selected companies.</p>
                    <form class="mui-form" @submit.prevent>
                        <template v-for="deductionType in customizableSelectedTypes" :key="deductionType.id">
                            <div class="form-row" v-if="canCustomize(deductionType, 'name')">
                                <div class="col">
                                    <form-input
                                        v-model="formData.customizations_by_deduction_type[deductionType.id].name"
                                        :errors="errors.name"
                                        type="text"
                                        :label="`Name (base: ${deductionType.name})`"
                                    />
                                </div>
                            </div>

                            <div class="form-row mb-2" v-if="canCustomize(deductionType, 'for_primary_benefit_of_employer')">
                                <div class="col">
                                    <form-input
                                        v-model="formData.customizations_by_deduction_type[deductionType.id].for_primary_benefit_of_employer"
                                        :errors="errors.for_primary_benefit_of_employer"
                                        type="checkbox"
                                        :label="`For Primary Benefit of Employer (${deductionType.name})`"
                                    />
                                </div>
                            </div>
                        </template>
                    </form>
                </div>

                <div v-if="step == 'positions' && !editingDeductionType">
                    <p>For each deduction, select which positions can use it.</p>
                    <table class="table css-table widget-table table-fixed mb-2">
                        <thead>
                            <tr>
                                <th>Deduction</th>
                                <th class="cw-6 text-right">Positions</th>
                            </tr>
                        </thead>
                        <tbody v-for="deductionType in selectedDeductionTypes">
                            <tr>
                                <td class="truncate-wrapper">
                                    <a href @click.prevent="editDeductionTypePositions(deductionType)" class="truncate-content">
                                        <span>{{ displayName(deductionType) }}</span>
                                    </a>
                                </td>
                                <td class="text-right">
                                    <span v-if="formData.default_for_all_positions_by_deduction_type[deductionType.id]">All</span>
                                    <span v-else>{{ formData.positions_by_deduction_type[deductionType.id] ? formData.positions_by_deduction_type[deductionType.id].length : '' }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="step == 'positions' && editingDeductionType">
                    <form-input
                        v-model="formData.default_for_all_positions_by_deduction_type[editingDeductionType.id]"
                        type="checkbox"
                        label="Available to all positions"
                        helper-text="Every position at the selected companies can use this deduction, including positions added later."
                    />

                    <template v-if="!formData.default_for_all_positions_by_deduction_type[editingDeductionType.id]">
                        <p>Select the positions that can use this deduction.</p>
                        <position-selector :all-positions="allCompanyPositions" :companies="client.companies" v-model="formData.positions_by_deduction_type[editingDeductionType.id]"/>
                    </template>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button v-if="step == 'companies'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-else-if="step == 'positions' && editingDeductionType" type="button" class="btn btn-outline-primary" @click.prevent="editingDeductionType = null">
                    Back
                </button>
                <button v-else type="button" class="btn btn-outline-primary" @click.prevent="prevStep">
                    Back
                </button>

                <button v-if="step == 'positions' && editingDeductionType" type="button" class="btn btn-primary" @click.prevent="editingDeductionType = null">
                    Done
                </button>
                <button v-else-if="step == 'positions' && !editingDeductionType" type="button" class="btn btn-primary" @click.prevent="onSubmitDeductionTypes()">
                    Save & Exit
                </button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="nextStep">
                    Continue
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'
import DeductionSelector from '@/pages/client/Settings/components/DeductionSelector'

export default {
    props: ['client', 'clientDeductionTypes'],
    mixins: [FormMixin, ModalMixin],
    components: {PositionSelector, CompanySelector, DeductionSelector},
    data() {
        return {
            steps: ['companies', 'deduction-types', 'customize', 'positions'],
            step: 'companies',
            formData: this.makeFormData(),
            globalDeductionTypes: [],
            allPositions: [],
            positionsLoaded: false,
            globalDeductionTypesLoaded: false,
            editingDeductionType: null
        }
    },
    mounted() {
        this.getPositions()
        this.getGlobalDeductionTypes()
    },
    watch: {
        dataLoaded(val) {
            if (val) {
                this.$store.dispatch('STOP_LOADING')
            }
        }
    },
    computed: {
        dataLoaded() {
            return this.positionsLoaded && this.globalDeductionTypesLoaded
        },
        allCompanyPositions() {
            if (this.formData.companies) {
                return this.allPositions.filter(p => this.formData.companies.includes(p.company_id))
            }
            return []
        },
        selectableDeductionTypes() {
            // filter out deduction types that already exist for at least one selected company,
            // unless the type allows a company to hold more than one
            let existingDeductionTypeIds = this.clientDeductionTypes.filter(d => this.formData.companies.includes(d.company_id)).map(d => d.deduction_type_id)
            return this.globalDeductionTypes.filter(d => d.allow_multiple_per_company || !existingDeductionTypeIds.includes(d.id))
        },
        selectedDeductionTypes() {
            return this.globalDeductionTypes.filter(d => this.formData.deduction_types.includes(d.id))
        },
        customizableSelectedTypes() {
            return this.selectedDeductionTypes.filter(d => d.fields_to_customize && d.fields_to_customize.length)
        },
        hasCustomizableFields() {
            return this.customizableSelectedTypes.length > 0
        },
    },
    methods: {
        getPositions() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => {return a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1})
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        getGlobalDeductionTypes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/admin/tax-engine/deduction-types`).then(resp => {
                this.globalDeductionTypes = resp.filter(d => {
                    return !['deferred-comp', 'insurance', 'retirement-loan'].includes(d.category)
                })
                this.globalDeductionTypesLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        makeFormData() {
            const data = {
                companies: [],
                deduction_types: [],
                customizations_by_deduction_type: {},
                positions_by_deduction_type: {},
                default_for_all_positions_by_deduction_type: {}
            }

            return data
        },
        resetErrors() { // TODO KB consider adding this to FormMixin
            this.errors =  {}
            this.formErrors = []
        },
        canCustomize(deductionType, fieldName) {
            return deductionType.fields_to_customize?.includes(fieldName) || false
        },
        displayName(deductionType) {
            return this.formData.customizations_by_deduction_type[deductionType.id]?.name || deductionType.name
        },
        nextStep() {
            let idx = this.steps.indexOf(this.step) + 1
            if (this.steps[idx] == 'customize' && !this.hasCustomizableFields) {
                idx++
            }
            this.step = this.steps[idx]
        },
        prevStep() {
            let idx = this.steps.indexOf(this.step) - 1
            if (this.steps[idx] == 'customize' && !this.hasCustomizableFields) {
                idx--
            }
            this.step = this.steps[idx]
        },
        onUpdateSelectedDeductionTypes(deductionTypeIds) {
            deductionTypeIds.forEach(deductionTypeId => {
                if (deductionTypeId) {
                    this.formData.positions_by_deduction_type[deductionTypeId] = this.formData.positions_by_deduction_type[deductionTypeId] || []
                    this.formData.default_for_all_positions_by_deduction_type[deductionTypeId] = this.formData.default_for_all_positions_by_deduction_type[deductionTypeId] || false
                    if (!this.formData.customizations_by_deduction_type[deductionTypeId]) {
                        const deductionType = this.globalDeductionTypes.find(d => d.id == deductionTypeId)
                        this.formData.customizations_by_deduction_type[deductionTypeId] = {
                            name: '',
                            for_primary_benefit_of_employer: deductionType ? deductionType.for_primary_benefit_of_employer : false,
                        }
                    }
                }
            })
        },
        editDeductionTypePositions(deductionType) {
            this.editingDeductionType = deductionType
        },
        getFormData() {
            return {
                companies: this.formData.companies,
                deduction_types: this.formData.deduction_types,
                customizations_by_deduction_type: this.formData.customizations_by_deduction_type,
                positions_by_deduction_type: this.formData.positions_by_deduction_type,
                default_for_all_positions_by_deduction_type: this.formData.default_for_all_positions_by_deduction_type
            }
        },
        onSubmitDeductionTypes() {
            this.$store.dispatch('START_LOADING')
            this.resetErrors()
            return this.$api.post(`/clients/${this.client.id}/settings/deduction-types`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        }
    }
}
</script>
